import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Copy, CheckCircle2, QrCode, Clock } from "lucide-react";

const fmt = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

// order: { order_id, qr_code, qr_code_base64, amount, status, ... }
export default function PixDialog({ open, onOpenChange, order, note, onApproved }) {
  const [status, setStatus] = useState(order?.status || "pending");
  const [copied, setCopied] = useState(false);
  const approvedRef = useRef(false);

  useEffect(() => {
    setStatus(order?.status || "pending");
    approvedRef.current = false;
  }, [order?.order_id, order?.status]);

  useEffect(() => {
    if (!open || !order?.order_id) return;
    if (status === "approved") return;
    const id = setInterval(async () => {
      try {
        const r = await api.get(`/payments/pix/${order.order_id}`);
        setStatus(r.data.status);
        if (r.data.status === "approved" && !approvedRef.current) {
          approvedRef.current = true;
          clearInterval(id);
          toast.success("Pagamento Pix confirmado!");
          onApproved && onApproved();
        }
      } catch {
        /* keep polling */
      }
    }, 4000);
    return () => clearInterval(id);
  }, [open, order?.order_id, status, onApproved]);

  const copy = () => {
    if (!order?.qr_code) return;
    navigator.clipboard.writeText(order.qr_code);
    setCopied(true);
    toast.success("Código Pix copiado!");
    setTimeout(() => setCopied(false), 2500);
  };

  const approved = status === "approved";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="pix-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <QrCode className="w-5 h-5 text-brand-blue" /> Pagamento via Pix
          </DialogTitle>
          <DialogDescription>
            {order ? `Valor: ${fmt(order.amount)}` : "Gerando cobrança..."}
          </DialogDescription>
        </DialogHeader>

        {!order ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-7 h-7 animate-spin text-brand-blue" />
          </div>
        ) : approved ? (
          <div className="flex flex-col items-center py-8 text-center" data-testid="pix-approved">
            <CheckCircle2 className="w-16 h-16 text-brand-green" />
            <p className="mt-4 font-heading font-bold text-lg text-brand-ink">Pagamento confirmado!</p>
            <p className="text-sm text-brand-muted mt-1">Seu acesso já foi liberado.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {order.qr_code_base64 && (
              <div className="flex justify-center">
                <img
                  src={`data:image/png;base64,${order.qr_code_base64}`}
                  alt="QR Code Pix"
                  className="w-52 h-52 rounded-xl border border-slate-200"
                  data-testid="pix-qr-image"
                />
              </div>
            )}
            <div>
              <p className="text-xs text-brand-muted mb-1.5">Pix Copia e Cola</p>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={order.qr_code || ""}
                  className="flex-1 text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 h-10 truncate"
                  data-testid="pix-copia-cola"
                />
                <Button onClick={copy} size="icon" variant="outline" className="rounded-lg h-10 w-10 shrink-0" data-testid="pix-copy-btn">
                  {copied ? <CheckCircle2 className="w-4 h-4 text-brand-green" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-brand-muted" data-testid="pix-status">
              <Loader2 className="w-4 h-4 animate-spin" />
              Aguardando pagamento...
            </div>
            {note && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
                <Clock className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{note}</span>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
